import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import type { TotpSetup } from '../auth/types';
import { ConfirmDialog } from '../components/Modal';
import { Panel } from '../components/Panel';
import { ErrorState, Loading, errorMessage } from '../components/States';
import { TotpEnrolment } from '../components/TotpEnrolment';
import { useToast } from '../components/Toast';
import { PageHeader } from '../layout/PageHeader';

const ROLE_LABEL: Record<string, string> = {
  'msp-admin': 'MSP administrator (all tenants)',
  'tenant-admin': 'Administrator',
  'tenant-viewer': 'Read-only',
};

export function AccountPage({ mspName }: { mspName: string }) {
  const auth = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState<{ totpEnabled: boolean } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [starting, setStarting] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const load = () => {
    setError(null);
    auth.getMfaStatus().then(setStatus).catch(setError);
  };
  useEffect(load, [auth]);

  const startSetup = async () => {
    setStarting(true);
    try {
      setSetup(await auth.setupTotp());
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    } finally {
      setStarting(false);
    }
  };

  const disable = async () => {
    setDisabling(true);
    try {
      await auth.disableTotp();
      toast.push('Two-step verification turned off', 'success');
      setConfirmDisable(false);
      load();
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    } finally {
      setDisabling(false);
    }
  };

  const user = auth.user;

  return (
    <>
      <PageHeader title="Account & security" subtitle="Your sign-in details and two-step verification." />
      <div className="grid-2">
        <Panel title="Account">
          <dl className="kv">
            <dt>Email</dt>
            <dd>{user?.email ?? '—'}</dd>
            <dt>Role</dt>
            <dd>{user ? (ROLE_LABEL[user.role] ?? user.role) : '—'}</dd>
            <dt>Dashboard</dt>
            <dd>{mspName}</dd>
          </dl>
          <p className="small muted" style={{ marginTop: 12 }}>
            To change your password, sign out and use "Forgot password?" on the sign-in page.
          </p>
        </Panel>

        <Panel
          title="Two-step verification"
          actions={status ? (status.totpEnabled ? <span className="pill"><ShieldCheck size={14} /> On</span> : <span className="pill"><ShieldOff size={14} /> Off</span>) : null}
        >
          {error ? <ErrorState error={error} retry={load} /> : !status ? <Loading /> : setup ? (
            <TotpEnrolment
              setup={setup}
              onVerify={async (code) => {
                await auth.verifyTotpSetup(code);
                toast.push('Two-step verification is on', 'success');
                setSetup(null);
                load();
              }}
              onCancel={() => setSetup(null)}
            />
          ) : status.totpEnabled ? (
            <div className="stack">
              <p className="secondary">
                An authenticator app is registered. You will be asked for a code from it every time you sign in.
              </p>
              <div className="row">
                <button type="button" className="btn" onClick={startSetup} disabled={starting}>
                  {starting ? 'Preparing…' : 'Register a different app'}
                </button>
                <button type="button" className="btn btn--danger" onClick={() => setConfirmDisable(true)}>
                  Turn off
                </button>
              </div>
            </div>
          ) : (
            <div className="stack">
              <p className="secondary">
                Protect your account with a one-time code from an authenticator app on your phone in addition to your password.
              </p>
              <div>
                <button type="button" className="btn btn--primary" onClick={startSetup} disabled={starting}>
                  {starting ? 'Preparing…' : 'Set up authenticator app'}
                </button>
              </div>
            </div>
          )}
        </Panel>
      </div>
      {confirmDisable ? (
        <ConfirmDialog
          title="Turn off two-step verification"
          message="Your account will be protected by your password only. If your organisation requires two-step verification you will be asked to set it up again at your next sign-in."
          confirmLabel="Turn off"
          danger
          busy={disabling}
          onConfirm={disable}
          onClose={() => setConfirmDisable(false)}
        />
      ) : null}
    </>
  );
}
